import React, { Suspense, useState } from 'react'
import { gql, useQuery } from 'urql'
import { useURLParam } from '../actions'
import ListPageControls from '../lists/ListPageControls'
import Search from '../util/Search'
import FlatList from '../lists/FlatList'
import ScheduleSelectionCreateDialog from './ScheduleSelectionCreateDialog'
import Chip from '@mui/material/Chip'

const query = gql`
  query scheduleSelectionsQuery($input: ScheduleSelectionSearchOptions) {
    scheduleSelections(input: $input) {
      nodes {
        id
        name
        status
        startTime
        numRounds
        rotationLengthDays
        createdAt
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

function statusColor(
  status: string,
): 'default' | 'primary' | 'success' | 'warning' {
  switch (status) {
    case 'in_progress':
      return 'primary'
    case 'completed':
      return 'success'
    case 'cancelled':
      return 'warning'
    default:
      return 'default'
  }
}

function statusLabel(status: string): string {
  switch (status) {
    case 'setup':
      return 'Setup'
    case 'in_progress':
      return 'In Progress'
    case 'completed':
      return 'Completed'
    case 'cancelled':
      return 'Cancelled'
    default:
      return status
  }
}

const context = { suspense: false }

export default function ScheduleSelectionList(): React.JSX.Element {
  const [search] = useURLParam<string>('search', '')
  const [create, setCreate] = useState(false)
  const [cursor, setCursor] = useState('')

  const inputVars = {
    search,
    after: cursor,
  }

  const [q] = useQuery<{ scheduleSelections: any }>({
    query,
    variables: { input: inputVars },
    context,
  })

  const nextCursor = q.data?.scheduleSelections.pageInfo.hasNextPage
    ? q.data?.scheduleSelections.pageInfo.endCursor
    : ''

  useQuery({
    query,
    variables: { input: { ...inputVars, after: nextCursor } },
    context,
    pause: !nextCursor,
  })

  return (
    <React.Fragment>
      <Suspense>
        {create && (
          <ScheduleSelectionCreateDialog onClose={() => setCreate(false)} />
        )}
      </Suspense>
      <ListPageControls
        createLabel='Schedule Selection'
        nextCursor={nextCursor}
        onCursorChange={setCursor}
        loading={q.fetching}
        onCreateClick={() => setCreate(true)}
        slots={{
          search: <Search />,
          list: (
            <FlatList
              emptyMessage='No schedule selections'
              items={
                q.data?.scheduleSelections.nodes.map((s: any) => ({
                  title: s.name,
                  subText: `${s.rotationLengthDays}-day blocks, ${s.numRounds} rounds`,
                  url: s.id,
                  secondaryAction: (
                    <Chip
                      size='small'
                      label={statusLabel(s.status)}
                      color={statusColor(s.status)}
                    />
                  ),
                })) || []
              }
            />
          ),
        }}
      />
    </React.Fragment>
  )
}
